import { Refresh } from '@mui/icons-material';
import { type ButtonProps, CircularProgress } from '@mui/material';
import React, { type CSSProperties } from 'react';

import { EStylings } from '../enums/EStylings';
import type { IRequestState } from '../models/IRequestState';
import CButtonDownload from './CButtonDownload';

CButton.Download = CButtonDownload;

interface props extends Pick<ButtonProps, 'onMouseEnter' | 'onMouseLeave' | 'onClick' | 'disabled' | 'className'> {
  children?: React.ReactNode;
  propagateClick?: boolean;
  style?: Pick<
    CSSProperties,
    | 'minHeight'
    | 'fontWeight'
    | 'marginBottom'
    | 'paddingTop'
    | 'borderRadius'
    | 'cursor'
    | 'color'
    | 'minWidth'
    | 'borderWidth'
    | 'width'
    | 'justifyContent'
    | 'aspectRatio'
    | 'padding'
    | 'textAlign'
    | 'position'
  >;
  icons?: {
    start?: JSX.Element;
    end?: JSX.Element;
  };
  isLoading?: boolean;
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'small' | 'medium' | 'large' | 'textfield';
  submodule?: boolean;
  isForm?: boolean;
  state?: IRequestState;
}

export default function CButton(props: props) {
  const { variant = 'secondary', isForm = false } = props;

  const handleClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
    if (props.propagateClick === false) ev.stopPropagation();
    if (props.disabled) return;
    props.onClick?.(ev);
  };

  const resolvedState = {
    isLoading: props.state?.isLoading ?? props.isLoading ?? false,
    isError: props.state?.isError ?? false
  };

  const minHeight = isForm ? EStylings.FORM_ELEMENT_HEIGHT : '2.5rem';

  // Standard variants (primary, secondary, danger)
  let variantClasses: string;
  switch (variant) {
    case 'primary':
      variantClasses = 'text-teal bg-teal bg-opacity-10 hover:bg-opacity-20 hover:border-teal';
      break;
    case 'danger':
      variantClasses = 'border border-red-600 text-red-600 bg-red-600 bg-opacity-0 hover:bg-opacity-10';
      break;
    default: // 'secondary'
      variantClasses = 'hover:text-teal bg-teal bg-opacity-0 hover:bg-opacity-10 hover:border-teal';
      break;
  }

  let classNames = [`rounded-md py-2 px-4 uppercase inline-flex items-center ${variantClasses}`];

  if (props.className) {
    classNames.push(props.className);
  }

  if (props.disabled) {
    classNames = classNames
      .join(' ')
      .split(' ')
      .filter((e) => !e.includes('hover:'));
  }
  if (resolvedState.isLoading) classNames.push('pointer-events-none');

  const StartIcon = () => (
    <span className="inline relative top-[-1px] text-[120%] -mx-1 pointer-events-none">
      {resolvedState.isLoading && (
        <div className="-mb-1 px-0.5">
          <CircularProgress size="1rem" color="inherit" className="leading-3" />
          &nbsp;
        </div>
      )}
      {!resolvedState.isLoading && !resolvedState.isError && props.icons?.start}
      {!resolvedState.isLoading && resolvedState.isError && <Refresh />}
    </span>
  );

  const EndIcon = () => (
    <span className="inline relative top-[-1px] text-[120%] -mx-1 pointer-events-none">
      {!resolvedState.isLoading && !resolvedState.isError && props.icons?.end}
      {!resolvedState.isLoading && resolvedState.isError && <Refresh />}
    </span>
  );

  const Spacer = () => <>&nbsp;&nbsp;</>;

  return (
    <button
      className={classNames.join(' ')}
      style={{
        cursor: (props.disabled ?? resolvedState.isLoading) ? 'default' : 'pointer',
        opacity: props.disabled ? 0.5 : 1,
        minHeight,
        hyphens: 'none',
        fontWeight: 600,
        ...props.style
      }}
      onClick={handleClick}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
    >
      {(props.icons?.start ?? resolvedState.isLoading ?? resolvedState.isError) && <StartIcon />}
      {(props.icons?.start ?? resolvedState.isLoading ?? resolvedState.isError) && props.children && <Spacer />}
      {props.children && <span>{props.children}</span>}
      {props.icons?.end && props.children && <Spacer />}
      {props.icons?.end && <EndIcon />}
    </button>
  );
}
